import { useCallback, useEffect, useState } from 'react';
import { identifyScenario, track } from '@/lib/analytics';
import { persistence } from '@/persistence';
import { useFunnelStore } from '@/store/useFunnelStore';

/**
 * Loads one scenario into the store and reports how that went.
 *
 * **This fails closed.** If the scenario service cannot be reached, the caller
 * does not open on defaults; it refuses and offers a retry. That is a deliberate
 * choice with a real cost: an API outage takes the tool down rather than
 * degrading it, so a brief blip during a live meeting means no diagram at all.
 * It is the right trade only because the alternative proved worse in practice —
 * failing open silently let a CORS misconfiguration disable the provisioning
 * gate, and every access code worked for hours without anyone noticing.
 *
 * The two refusals are kept distinct on purpose. "Unrecognized code" and "cannot
 * reach the service" send someone to look in completely different places, and
 * claiming the first when we only know the second would waste their time.
 *
 * Extracted from `ProspectCanvas` so the admin console reuses these semantics
 * rather than growing a second, quietly divergent copy of them.
 */
export type ScenarioPhase = 'loading' | 'ready' | 'unknown-code' | 'unreachable';

export function useScenarioSession(scenarioId: string | null): {
  phase: ScenarioPhase;
  retry: () => void;
} {
  const hydrate = useFunnelStore((s) => s.hydrate);
  const resetEditSession = useFunnelStore((s) => s.resetEditSession);
  const [phase, setPhase] = useState<ScenarioPhase>('loading');
  /** Bumped by the retry button to re-run the load effect. */
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => {
    setPhase('loading');
    setAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!scenarioId) return;
    let canceled = false;

    /* Zero the singleton before the load, so a previous scenario's `revision`
       and `params` cannot follow this one in. A no-op on a first mount; the whole
       point when switching between scenarios in the admin console. */
    resetEditSession();

    // The access code IS the prospect identity, so replays group per prospect.
    identifyScenario(scenarioId);

    void (async () => {
      let result;
      try {
        result = await persistence.load(scenarioId);
      } catch {
        // A throw from the port is a transport problem, never a verdict on the
        // code — so it must not be reported as an unrecognized one.
        result = { status: 'unavailable' } as const;
      }
      if (canceled) return;

      switch (result.status) {
        case 'found':
          hydrate(result.params);
          track('scenario_opened', { source: persistence.kind });
          setPhase('ready');
          return;

        case 'absent':
          /* With a service configured, an absent scenario means the code was
             never provisioned. Running local-only there is no provisioning to
             speak of, and a new code is simply a new scenario. */
          if (persistence.kind === 'http') {
            track('unknown_code');
            setPhase('unknown-code');
            return;
          }
          hydrate(null);
          track('scenario_opened', { source: 'default' });
          setPhase('ready');
          return;

        case 'unavailable':
          if (persistence.kind === 'http') {
            track('service_unreachable');
            setPhase('unreachable');
            return;
          }
          // Local-only: unreadable storage is not a reason to withhold the tool.
          hydrate(null);
          track('scenario_opened', { source: 'default' });
          setPhase('ready');
          return;
      }
    })();

    return () => {
      canceled = true;
    };
  }, [scenarioId, hydrate, resetEditSession, attempt]);

  return { phase, retry };
}
